import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Alert, Paper, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Eaton } from '@brightlayer-ui/icons-mui';

// Domain to IdP mapping based on email domain
// Default: Okta handles all domains except companyABC.tst
// NOTE: Keys must be lowercase because domain is extracted with .toLowerCase()
const DOMAIN_IDP_MAP = {
    'companyabc.tst': 'test-keycloak'
    // All other domains default to 'okta'
};

const StyledPaper = styled(Paper)(({ theme }) => ({
    width: 500,
    minWidth: 500,
    maxWidth: 500,
    minHeight: 600,
    margin: '100px auto',
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxShadow: theme.shadows[3]
}));

const StyledForm = styled('form')(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(2)
}));

const EmailFirstLogin = ({ onEmailSubmit }) => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Log when component mounts
    React.useEffect(() => {
        console.log('✅ EmailFirstLogin component mounted (Keycloak email-first login active)');
        console.log('📋 DOMAIN_IDP_MAP configuration:', DOMAIN_IDP_MAP);
        return () => {
            console.log('❌ EmailFirstLogin component unmounting');
        };
    }, []);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Validate email format
            if (!validateEmail(email)) {
                setError('Please enter a valid email address');
                setLoading(false);
                return;
            }

            // Extract domain from email
            const domain = email.toLowerCase().split('@')[1];

            // Map domain to IdP (default to 'okta' for all domains except companyABC.tst)
            const idpHint = DOMAIN_IDP_MAP[domain] || 'okta';

            console.log('🔍 ======================================');
            console.log('🔍 EMAIL-FIRST LOGIN - DOMAIN ROUTING');
            console.log('🔍 ======================================');
            console.log('📧 Full email entered:', email);
            console.log('🌐 Extracted domain:', domain);
            console.log('📋 Available domain mappings:', JSON.stringify(DOMAIN_IDP_MAP, null, 2));
            console.log('🎯 Checking if domain matches "companyabc.tst":', domain === 'companyabc.tst');
            console.log('🔑 Mapped IdP:', idpHint);
            if (idpHint === 'test-keycloak') {
                console.log('✅ ✅ ✅ ROUTING TO TEST KEYCLOAK ✅ ✅ ✅');
            } else if (idpHint === 'okta') {
                console.log('⚠️  ⚠️  ⚠️  ROUTING TO OKTA (default) ⚠️  ⚠️  ⚠️');
            }
            console.log('🔍 ======================================');

            // Call parent component with email and IdP hint
            if (onEmailSubmit) {
                onEmailSubmit(email, idpHint);
            }
        } catch (err) {
            console.error('Email validation error:', err);
            setError('An error occurred. Please try again.');
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && email) {
            handleSubmit(e);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5'
            }}
        >
            <StyledPaper elevation={3}>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                    <Eaton style={{ fontSize: '80px', color: '#007BC1' }} />
                </Box>

                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Sign In
                </Typography>

                <Typography variant="body1" color="textSecondary" align="center" sx={{ mb: 3 }}>
                    Enter your email address to continue
                </Typography>

                <StyledForm onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        id="email"
                        label="Email Address"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="user@eaton.com"
                        disabled={loading}
                        autoFocus
                        variant="outlined"
                        margin="normal"
                        required
                    />

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        disabled={loading || !email}
                        sx={{ mt: 3, mb: 2, py: 1.5, minHeight: 42 }}
                    >
                        {loading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                                <span>Signing in...</span>
                            </Box>
                        ) : (
                            'Continue'
                        )}
                    </Button>

                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography
                            component="a"
                            href="/forgot-password"
                            variant="body2"
                            sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            Forgot Password?
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                            Need an account?{' '}
                            <Typography
                                component="a"
                                href="/self-registration"
                                variant="body2"
                                sx={{
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Create Account
                            </Typography>
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography
                            component="a"
                            href="/eula.htm"
                            variant="body2"
                            sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            Terms and Conditions
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Authentication:
                        </Typography>
                        <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                            <Typography component="li" variant="body2" color="textSecondary">
                                @companyABC.tst → Test Keycloak
                            </Typography>
                            <Typography component="li" variant="body2" color="textSecondary">
                                All other domains → Okta
                            </Typography>
                        </Box>
                    </Box>
                </StyledForm>
            </StyledPaper>
        </Box>
    );
};

export default EmailFirstLogin;
